import type { CountryConfig } from './types.js';
import { uzPublic } from './uz.public.js';
import { classifyTitleToIkpu, resolveIkpu } from './uz.classifier.js';

// The FULL UZ config = the web-safe `uzPublic` fields (uz.public.ts) plus the
// server-only fields added here (sourceKeys, companyId, classifier). The web
// bundle imports `uzPublic` via `publicCountryConfigs`, never this file, so the
// classifier's keyword tables and the ИНН rules stay out of the browser.

/**
 * Shape validation for an Uzbek ИНН / СТИР.
 *
 * ## Why there is no check-digit verification here
 *
 * Published guides state the 9th digit "serves as a check digit", but none
 * publish the algorithm, so it was DERIVED EMPIRICALLY from 8,000 real ИНН
 * taken from our own `organizations` table. Every candidate failed:
 *
 *  - No linear rule `Σ wᵢdᵢ ≡ 0` exists for ANY modulus tried (11, 7, 13, 3,
 *    101) — the null space of the sample matrix is trivial in every case, which
 *    rules out the entire family of weighted-sum check digits.
 *  - No affine rule `Σ wᵢdᵢ + k ≡ 0` either.
 *  - Luhn matches 10.1% of real ИНН, i.e. exactly chance for a base-10 digit.
 *
 * So a checksum cannot be reconstructed from the data we hold, and guessing one
 * is strictly worse than having none: a wrong formula silently REJECTS
 * legitimate taxpayers, locking real customers out of the ИП plans and the
 * company step, and the failure looks like "the site says my ИНН is invalid"
 * with nothing in the logs. Accepting a junk ИНН is a recoverable annoyance;
 * rejecting a valid one is a lost customer.
 *
 * If real verification is needed, the authoritative source is the Tax
 * Committee's registry lookup (`my.soliq.uz`), which answers for a real ИНН and
 * 404s otherwise. It is IP-restricted to Uzbekistan — which production
 * satisfies — so it belongs on the server, not in a shared validator.
 *
 * ## What IS rejected
 *
 * Placeholders, which are demonstrably a live problem rather than a
 * hypothetical. `users.tin` in production already holds `111111111` (twice) and
 * `123456789`, and the SAME values pollute `tender_results.winner_tin` — so the
 * company step counted them as awards and told people with no company that they
 * had "won" 34 tenders. That fabrication then triggered the supplier link,
 * enrolling them in matching for categories derived from unrelated contracts.
 *
 * ⚠️ Deliberately NOT rejected: a leading digit outside 2/3. Real ИНН cluster
 * there (97.8% of our 30,445 organisations) and it is a tempting rule, but
 * 4/5/6/9/1/7/8 all occur on genuine records — enforcing it would reject ~665
 * real organisations. Measured before ruling it out, not assumed.
 *
 * Moved verbatim from `services/api/src/modules/auth/validate-tin.ts` — that
 * file now delegates here via `CountryConfig.companyId.validate`.
 */

/** All nine digits identical: `000000000`, `111111111`, … */
const TIN_ALL_SAME = /^(\d)\1{8}$/;

/** Runs like `123456789` or `987654321`, in either direction, from any start. */
function isSequentialTin(tin: string): boolean {
  const step = (a: string, b: string) => Number(b) - Number(a);
  const first = step(tin[0], tin[1]);
  if (first !== 1 && first !== -1) return false;
  for (let i = 1; i < tin.length - 1; i++) {
    if (step(tin[i], tin[i + 1]) !== first) return false;
  }
  return true;
}

export const uzConfig: CountryConfig = {
  ...uzPublic,
  // Every `TenderSource.key` currently enabled in prod (33, verified 2026-09-08
  // against `buildAdapterMap()` in services/api/scripts/scheduler.ts, the seed
  // registry in services/api/prisma/seed.ts, and the CLAUDE.md status note —
  // `ted` and `eauksion` are deliberately excluded, matching their DB-disabled
  // state). Grouped by `TenderScope` for readability only; the scheduler
  // treats this as one flat allow-list.
  sourceKeys: [
    // gov
    'tender-mc',
    'etender-uzex',
    'xarid-prequest',
    'uzex-xarid',
    'xt-xarid',
    'ebirja',
    'cooperation-auction',
    'farma-uzex',
    // ifi
    'worldbank',
    'adb',
    'ungm',
    'undp',
    'aiib',
    'isdb',
    'ebrd',
    'uzrd',
    'osce',
    // corporate
    'kapitalbank',
    'aloqabank',
    'trustbank',
    'uzairways',
    'beeline',
    'tenderweek',
    'getbiz',
    'ucell',
    'b2biz',
    'sqb',
    'agmk',
    'uzgidro',
    'ofb',
    'saneg',
    'uzairports',
    'uzgidroqurilish',
    // Uz-Kor Gas Chemical (uz-kor.com). Shipped in !516 while this allow-list
    // was landing in parallel, and never added — so the scheduler skipped the
    // adapter silently and the source sat at 0 lots for two cycles after its
    // `tender_sources` row was enabled (2026-09-11). An adapter is live only when
    // it is registered in `buildAdapterMap()`, has an enabled row AND is listed
    // here — all three, or it is skipped without an error.
    'uzkor',
    'uzrailway',
    'anorbank',
    // supply (kept out of browse/search, but still ingested)
    'cooperation',
  ],
  // `uzex-direct` is the direct-purchase feed (owner, 2026-10-10). No Uzbek row
  // carries a `method`, so no method marker: the concentration reads stay
  // exactly what they were.
  competition: {
    nonCompetitiveSources: ['uzex-direct'],
    nonCompetitiveMethodMarkers: [],
    unknownMethodIsCompetitive: true,
  },
  companyId: {
    label: 'ИНН',
    validate(tin) {
      if (!/^\d{9}$/.test(tin)) return false;
      if (TIN_ALL_SAME.test(tin)) return false;
      if (isSequentialTin(tin)) return false;
      return true;
    },
  },
  // ИКПУ product classifier. The keyword rules + resolve/classify logic live in
  // `uz.classifier.ts` (moved verbatim from the api's ikpu-classifier.ts, which
  // now re-exports them). Wrapped — not duplicated — so ingest behaviour stays
  // byte-identical.
  classifier: {
    resolve: (code, title) => resolveIkpu(code, title),
    classify: (title) => classifyTitleToIkpu(title),
  },
  // Owner, 2026-10-10: ACTIVE and ≥2 competitive awards in 24 months (plus the
  // grandfather list) — `company-index-rule.ts`.
  companyIndex: { minCompetitiveAwards: 2, windowMonths: 24, requireActiveStatus: true },
  // The API's award guard band, unchanged from its old hard-coded so'm literals
  // (`services/api/src/modules/catalog/award-sanity.ts`; its spec pins the numbers).
  awardPlausibility: { floor: 1_000, ceiling: 10_000_000_000_000 },
};
